import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import LandingPage from './pages/LandingPage';
import Register from './pages/Register';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Stores from './pages/Stores';
import Groups from './pages/Groups';
import MusicLibrary from './pages/MusicLibrary';
import Playlists from './pages/Playlists';
import Dayparting from './pages/Dayparting';
import Calendar from './pages/Calendar';
import Announcements from './pages/Announcements';
import Legal from './pages/Legal';
import Subscription from './pages/Subscription';
import Users from './pages/Users';
import AdminDashboard from './pages/AdminDashboard';
import Analytics from './pages/Analytics';
import Kiosk from './pages/Kiosk';
import SetupWizard from './pages/SetupWizard';
import Branding from './pages/Branding';
import Expired from './pages/Expired';
import Feedback from './pages/Feedback';
import AdminFeedback from './pages/AdminFeedback';
import Help from './pages/Help';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DashboardLayout from './components/DashboardLayout';
import '@/App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f0e8]">
        <div className="text-[#8a8477] font-mono">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Guard that restricts managers to only their allowed permissions.
// If a manager navigates (via URL) to a module they don't have permission for,
// redirect them to the first module they are allowed to see.
const ManagerRoute = ({ permission, children }) => {
  const { user } = useAuth();
  if (user?.role !== 'manager') return children;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  if (perms.includes(permission)) return children;
  const fallbackOrder = ['dashboard', 'library', 'playlists', 'dayparting', 'calendar', 'announcements', 'analytics', 'legal'];
  const pathMap = {
    dashboard: '/dashboard/', library: '/dashboard/library', playlists: '/dashboard/playlists',
    dayparting: '/dashboard/dayparting', calendar: '/dashboard/calendar', announcements: '/dashboard/announcements',
    analytics: '/dashboard/analytics', legal: '/dashboard/legal',
  };
  const firstAllowed = fallbackOrder.find((p) => perms.includes(p));
  return <Navigate to={firstAllowed ? pathMap[firstAllowed] : '/login'} replace />;
};

// Users (Team) page is accessible to both tenant_admin AND manager roles.
// Backend enforces the "managers can only add/edit/delete kiosk users" rule,
// so the frontend just gates access at the route level — no per-permission
// check needed.
const TenantOperatorRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'tenant_admin' || user?.role === 'manager' || user?.role === 'global_super_admin') return children;
  return <Navigate to="/dashboard/" replace />;
};

// Restrict a route to tenant_admin (plus global_super_admin for support-view scenarios).
// Everyone else lands on the dashboard root (managers) or login (unknown).
const TenantAdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'tenant_admin' || user?.role === 'global_super_admin') return children;
  return <Navigate to="/dashboard/" replace />;
};

function AppRoutes() {
  const { user } = useAuth();

  // "kiosk" role goes directly to kiosk for their assigned store
  const isKioskUser = user && user.role === 'kiosk';
  const isPendingPayment = user && user.tenant_status === 'pending_payment';
  const isBlocked = user && user.tenant_status === 'blocked';
  const isGlobalAdmin = user && user.role === 'global_super_admin';

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/blocked" element={<Navigate to="/expired" replace />} />
      <Route path="/expired" element={<Expired />} />
      <Route path="/kiosk/:storeId" element={
        <ProtectedRoute>
          <Kiosk />
        </ProtectedRoute>
      } />
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            {isKioskUser ? (
              <Navigate to={user.store_id ? `/kiosk/${user.store_id}` : '/expired'} replace />
            ) : isBlocked && !isGlobalAdmin ? (
              <Navigate to="/expired" replace />
            ) : isPendingPayment && !isGlobalAdmin ? (
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard/subscription" replace />} />
                  <Route path="/subscription" element={<Subscription />} />
                  <Route path="/legal" element={<Legal />} />
                  <Route path="*" element={<Navigate to="/dashboard/subscription" replace />} />
                </Routes>
              </DashboardLayout>
            ) : (
              <DashboardLayout>
                <Routes>
                  <Route path="/" element={<ManagerRoute permission="dashboard"><Dashboard /></ManagerRoute>} />
                  <Route path="/stores" element={<TenantAdminRoute><Stores /></TenantAdminRoute>} />
                  <Route path="/groups" element={<TenantAdminRoute><Groups /></TenantAdminRoute>} />
                  <Route path="/library" element={<ManagerRoute permission="library"><MusicLibrary /></ManagerRoute>} />
                  <Route path="/playlists" element={<ManagerRoute permission="playlists"><Playlists /></ManagerRoute>} />
                  <Route path="/dayparting" element={<ManagerRoute permission="dayparting"><Dayparting /></ManagerRoute>} />
                  <Route path="/calendar" element={<ManagerRoute permission="calendar"><Calendar /></ManagerRoute>} />
                  <Route path="/announcements" element={<ManagerRoute permission="announcements"><Announcements /></ManagerRoute>} />
                  <Route path="/legal" element={<ManagerRoute permission="legal"><Legal /></ManagerRoute>} />
                  <Route path="/subscription" element={<TenantAdminRoute><Subscription /></TenantAdminRoute>} />
                  <Route path="/users" element={<TenantOperatorRoute><Users /></TenantOperatorRoute>} />
                  <Route path="/analytics" element={<ManagerRoute permission="analytics"><Analytics /></ManagerRoute>} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/feedback" element={<AdminFeedback />} />
                  <Route path="/feedback" element={<Feedback />} />
                  <Route path="/help" element={<Help />} />
                  <Route path="/setup" element={<TenantAdminRoute><SetupWizard /></TenantAdminRoute>} />
                  <Route path="/branding" element={<TenantAdminRoute><Branding /></TenantAdminRoute>} />
                </Routes>
              </DashboardLayout>
            )}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
